import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useParentAuth } from "@/context/ParentAuthContext";
import { useCategories } from "@/context/CategoriesContext";
import { RegistrationIdentity } from "@/components/RegistrationIdentity";
import { RegistrationDetails } from "@/components/RegistrationDetails";
import { RegistrationValidation, registrationErrors, profileFieldsChanged } from "@/components/RegistrationValidation";
import { ProfileUpdatePrompt } from "@/components/ProfileUpdatePrompt";
import { OrderSummary, formatMoney } from "@/components/OrderSummary";
import { useRegistrationQuote } from "@/hooks/useRegistrationQuote";
import { toast } from "sonner";
import { ArrowRight, MapPin, Calendar, DollarSign, CreditCard } from "lucide-react";


function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

export default function Register() {
  const [params] = useSearchParams();
  const paramEventId = params.get("event") || "";
  const inviteToken = params.get("invite") || "";

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(paramEventId);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");

  const { parent } = useParentAuth();
  const { ALL_CATEGORIES, loading: categoryLoading, error: categoryError } = useCategories();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [f, setF] = useState({
    grade: "",
    previous_competitions: "",
    first_name: "",
    last_name: "",
    parent_selected_category: "",
    parent_name: "",
    contact_email: "",
    contact_phone: "",
    current_school: "",
    shirt_size: "",
  });

  useEffect(() => {
    let active = true;
    setEventId(paramEventId); setInviteEvent(null); setInviteEmail("");
    api.get("/events/public?kind=competition").then(({ data }) => {
      if (!active) return;
      const list = data || [];
      setEvents(list);
      if (!paramEventId && !inviteToken) setEventId(list[0]?.id || "");
    }).catch(() => { if (active) toast.error("Could not load competitions. Please try again."); });
    if (inviteToken) {
      api.get(`/events/invite/${inviteToken}`).then(({ data }) => {
        if (!active) return;
        setInviteEvent(data.event);
        setInviteEmail(data.invite?.email || "");
        setEventId(data.event.id);
        setF((prev) => ({ ...prev, contact_email: prev.contact_email || data.invite?.email || "" }));
      }).catch(() => { if (active) { setEventId(""); toast.error("This invitation is invalid or expired."); } });
    }
    return () => { active = false; };
  }, [paramEventId, inviteToken]);
  useEffect(() => {
    let active = true;
    api.get("/config/stripe").then(({ data }) => { if (active) setStripeCfg(data); })
      .catch(() => { if (active) toast.error("Could not load payment availability. Please try again."); });
    return () => { active = false; };
  }, []);

  const event = useMemo(() => inviteEvent || events.find((e) => e.id === eventId), [inviteEvent, events, eventId]);
  const { quote, loading: quoteLoading, error: quoteError, refresh: refreshQuote } = useRegistrationQuote(eventId, inviteToken);
  const child = children.find((item) => item.id === childId);
  const parentId = parent?.user_id;
  const parentEmail = parent?.email;
  const parentName = parent?.name;
  useEffect(() => {
    let active = true;
    setChildId(""); setChildren([]);
    if (parentId) {
      api.get("/parent/children").then(({ data }) => { if (active) setChildren(data || []); })
        .catch(() => { if (active) toast.error("Could not load saved cubers."); });
    }
    return () => { active = false; };
  }, [parentId]);
  useEffect(() => {
    if (parentEmail) setF((previous) => ({ ...previous, contact_email: previous.contact_email || parentEmail, parent_name: previous.parent_name || parentName || "" }));
  }, [parentEmail, parentName]);
  const chooseChild = (id) => {
    setChildId(id);
    const selected = children.find((item) => item.id === id);
    setF((previous) => ({ ...previous, previous_competitions: "", first_name: selected?.first_name || "", last_name: selected?.last_name || "", current_school: selected?.current_school || "", shirt_size: selected?.shirt_size || "", parent_selected_category: selected?.parent_selected_category || "", contact_email: selected ? parent.email : previous.contact_email }));
  };
  const requestId = useRef(crypto.randomUUID());
  useEffect(() => { requestId.current = crypto.randomUUID(); }, [eventId, childId, f]);
  const errors = registrationErrors(f, ALL_CATEGORIES, { savedChild: child, requireParentName: true });
  const formValid = Object.keys(errors).length === 0;
  const changedProfileFields = profileFieldsChanged(child, f);
  const ready = !!eventId && !!quote && !categoryLoading && !categoryError && (quote.is_free || (stripeCfg?.configured && stripeCfg?.accepting_payments));
  useEffect(() => { setAttempted(false); setShowProfilePrompt(false); }, [eventId, childId]);

  const goToCheckout = async (saveProfile = null) => {
    if (submitting) return;
    setAttempted(true);
    if (!formValid) {
      requestAnimationFrame(() => document.querySelector('[data-testid="reg-validation-summary"]')?.focus());
      return;
    }
    if (!ready) return;
    if (inviteToken && inviteEmail && f.contact_email.trim().toLowerCase() !== inviteEmail.toLowerCase()) {
      toast.error(`This invitation is for ${inviteEmail}. Use that email to register.`);
      return;
    }
    if (child && changedProfileFields.length && saveProfile === null) { setShowProfilePrompt(true); return; }
    setShowProfilePrompt(false);
    setSubmitting(true);
    try {
      const payload = { request_id: requestId.current, event_id: eventId, ...f, expected_total_cents: quote.total_cents, origin_url: window.location.origin, invite_token: inviteToken || undefined };
      if (child) {
        payload.update_profile_fields = saveProfile ? changedProfileFields : [];
        payload.child_id = child.id;
        delete payload.first_name; delete payload.last_name; delete payload.contact_email;
      }
      const { data } = await api.post(child ? "/parent/register-child" : "/payments/checkout", payload);
      if (data?.confirmation_url || data?.checkout_url) {
        window.location.href = data.confirmation_url || data.checkout_url;
      } else {
        toast.error("Could not start checkout.");
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((item) => item.msg.replace(/^Value error, /, "")).join(" ") : "Could not start checkout");
      if (err.response?.status === 409) refreshQuote();
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-24">
      <Link to="/competitions" className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C] hover:text-[#FF9F1C]">← Back to Competitions</Link>
      <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">Competition Registration</h1>
      <p className="mt-4 text-lg text-[#7A7D8C] max-w-xl" data-testid="registration-instructions">Enter the parent or guardian&apos;s contact information and the participant&apos;s details. Competitors must be in grades 3–12. Registration payments are non-refundable. Free competitions require no payment.</p>

      {inviteEvent && (
        <div className="mt-6 rounded-2xl bg-[#FDE74C]/40 border border-[#FDE74C] px-5 py-4 text-sm text-[#7A5F00]" data-testid="register-invite-banner">
          <b>Private invitation:</b> You&apos;re registering for the invite-only event <b>{inviteEvent.title}</b>. Use the invited email <b>{inviteEmail}</b>.
        </div>
      )}

      {events.length === 0 && !inviteEvent && (
        <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 px-5 py-6 text-[#7A7D8C]" data-testid="register-no-events">
          No upcoming competitions are open for registration right now. <Link to="/contact" className="text-[#FF9F1C] font-semibold">Contact us</Link> to be notified.
        </div>
      )}

      {(events.length > 0 || inviteEvent) && (
        <form noValidate onSubmit={(event) => { event.preventDefault(); goToCheckout(); }} className="mt-8 grid gap-6" data-testid="competition-registration-form">
          <RegistrationValidation prefix="reg" errors={attempted ? errors : {}} />
          <p className="text-xs text-[#7A7D8C]" data-testid="reg-required-note">Fields marked * are required.</p>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8 space-y-5" data-testid="register-form">
            <RegistrationIdentity value={f} onChange={(field, value) => setF((previous) => ({ ...previous, [field]: value }))} savedChild={child} errors={attempted ? errors : {}} emailReadOnly={!!child}>
              {parent && children.length > 0 && <div><label htmlFor="reg-saved-child" className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Saved cuber (optional)</label><select id="reg-saved-child" data-testid="reg-saved-child" value={childId} onChange={(event) => chooseChild(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm"><option value="">Enter a new cuber's details</option>{children.map((item) => <option key={item.id} value={item.id}>{item.full_name}</option>)}</select></div>}
            </RegistrationIdentity>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Parent / guardian name" value={f.parent_name} onChange={(v) => setF({ ...f, parent_name: v })} required tid="reg-parent" />
              <Field label="Phone (optional)" type="tel" value={f.contact_phone} onChange={(v) => setF({ ...f, contact_phone: v })} tid="reg-phone" />
            </div>
            <RegistrationDetails value={f} onChange={(field, value) => setF((previous) => ({ ...previous, [field]: value }))} errors={attempted ? errors : {}} />
            {!parent && <p className="text-xs text-[#7A7D8C]">Tip: <Link to="/parent/signup" data-testid="reg-create-account" className="font-semibold text-[#FF9F1C]">Create a parent account</Link> with this same email to claim data and view results.</p>}
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
            <label htmlFor="register-event" className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Which competition?</label>
            {inviteEvent ? <div className="mt-1 rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm font-semibold" data-testid="register-invite-event">{inviteEvent.title} · {inviteEvent.start_date}</div> : <select id="register-event" value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="register-event-select" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">{events.map((e) => <option key={e.id} value={e.id}>{`${e.title} · ${e.start_date}`}</option>)}</select>}
            {event && <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#1E1E24]"><span className="inline-flex items-center gap-1" data-testid="reg-event-date"><Calendar className="w-3.5 h-3.5" />{fmtDate(event.start_date)}</span>{event.location && <span className="inline-flex items-center gap-1" data-testid="reg-event-location"><MapPin className="w-3.5 h-3.5" />{event.location}</span>}{event.registration_fee != null && <span className="inline-flex items-center gap-1 font-bold text-[#B36906]" data-testid="reg-event-fee"><DollarSign className="w-3.5 h-3.5" />{Number(event.registration_fee).toFixed(2)} fee</span>}</div>}
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
            <h3 className="font-display font-black text-xl">Checkout</h3>
            {!quote?.is_free && <p className="mt-3 text-sm text-[#7A7D8C]" data-testid="reg-non-refundable">All registration payments are non-refundable.</p>}
            <div className="mt-4"><OrderSummary summary={quote} loading={quoteLoading} error={quoteError} onRetry={refreshQuote} prefix="registration-order" /></div>
            {quote?.is_free && <button type="submit" disabled={!ready || submitting} data-testid="free-registration-btn" className="mt-5 w-full rounded-full bg-[#FF9F1C] px-5 py-3 font-bold text-white disabled:opacity-50">{submitting ? "Registering…" : "Confirm free registration"}</button>}
            {!quote?.is_free && stripeCfg && !stripeCfg.configured && (
              <div className="mt-3 rounded-xl bg-[#FDE74C]/25 border border-[#FDE74C]/40 px-4 py-3 text-sm text-[#7A5F00]" data-testid="stripe-not-configured">
                Stripe payments aren't fully configured yet. Please <Link to="/contact" className="font-bold text-[#FF9F1C]">contact us</Link> to register manually.
              </div>
            )}
            {!quote?.is_free && stripeCfg && stripeCfg.configured && !stripeCfg.accepting_payments && (
              <div className="mt-3 rounded-xl bg-[#FDE74C]/25 border border-[#FDE74C]/40 px-4 py-3 text-sm text-[#7A5F00]" data-testid="stripe-test-mode-block">
                Online registration isn't open just yet — we're finishing final payment setup. Please <Link to="/contact" className="font-bold text-[#FF9F1C]">contact us</Link> at <a href="mailto:naptowncubers@gmail.com" className="font-bold text-[#FF9F1C]">naptowncubers@gmail.com</a> to sign up in the meantime.
              </div>
            )}
            {!quote?.is_free && stripeCfg && stripeCfg.configured && stripeCfg.accepting_payments && (
              <>
                <div className="mt-6" data-testid="payment-methods">
                  <div className="rounded-2xl border border-slate-200 p-5 bg-[#FDFFFC]">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-black text-[#7A7D8C]"><CreditCard className="w-3.5 h-3.5" /> Card via Stripe</div>
                    <p className="mt-2 text-xs text-[#7A7D8C]">All major cards, Apple Pay, Google Pay.</p>
                    <button
                      type="submit"
                      disabled={!ready || submitting}
                      data-testid="stripe-checkout-btn"
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_10px_24px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:hover:translate-y-0"
                    >
                      {submitting ? "Redirecting…" : quote ? `Pay ${formatMoney(quote.total_cents, quote.currency)}` : "Review total before payment"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {!formValid && <div className="mt-3 text-sm text-[#7A7D8C]" data-testid="reg-form-hint">Complete every required field above before continuing.</div>}
              </>
            )}
          </div>
        </form>
      )}
      <ProfileUpdatePrompt open={showProfilePrompt} child={child} value={f} fields={changedProfileFields} onChoose={goToCheckout} onCancel={() => setShowProfilePrompt(false)} />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, tid, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}{required && <span className="text-[#E71D36]"> *</span>}</label>
      <input data-testid={tid} type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
    </div>
  );
}
